import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, Check, ChefHat, Clock, CloudOff, MapPin, Phone,
  RefreshCw, Truck, UserCheck, UtensilsCrossed,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { formatTime12 } from "../lib/mealTypes";
import { ShareButton } from "../components/ShareButton";
import { todaySummary } from "../lib/shareText";
import { describeAge, fetchWithCache } from "../lib/offlineCache";

/**
 * The day-of screen.
 *
 * Every other page in this app is about what will happen. This one is for the
 * few hours when the event is actually running and someone is holding a phone
 * in a corridor while being interrupted.
 *
 * Design rules, in priority order:
 *   1. The next thing to do is visible without scrolling
 *   2. Touch targets are large enough to hit without looking carefully
 *   3. Calling the vendor is one tap, never a lookup
 *   4. It refreshes itself, because nobody remembers to pull down
 */

const STATUS = {
  pending: { label: "Pending", icon: Clock, tone: "bg-muted text-muted-foreground" },
  arrived: { label: "Arrived", icon: Truck, tone: "bg-blue-500/10 text-blue-600" },
  served: { label: "Served", icon: Check, tone: "bg-emerald-500/10 text-emerald-600" },
  issue: { label: "Issue", icon: AlertTriangle, tone: "bg-destructive/10 text-destructive" },
};

const REFRESH_MS = 60000;

function StatusPill({ status }) {
  const { label, icon: Icon, tone } = STATUS[status] || STATUS.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${tone}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function MealRow({ meal, canUpdate, onStatus, highlight }) {
  const timing = [meal.start_time, meal.end_time].filter(Boolean).map(formatTime12).join(" — ");
  return (
    <Card
      className={highlight ? "border-primary shadow-sm" : ""}
      data-testid={`today-meal-${meal.meal_id}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-foreground truncate">{meal.meal_type}</p>
            {timing && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {timing}
              </p>
            )}
          </div>
          <StatusPill status={meal.status} />
        </div>

        {meal.vendor_name && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground flex items-center gap-1.5 truncate">
              <ChefHat className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {meal.vendor_name}
            </p>
            {meal.vendor_phone && (
              // A link, not a handler — the OS dialer is the fastest path.
              <a href={`tel:${meal.vendor_phone}`} data-testid={`call-vendor-${meal.meal_id}`}>
                <Button variant="outline" size="sm" className="h-11 gap-1.5">
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call
                </Button>
              </a>
            )}
          </div>
        )}

        {meal.status_note && (
          <p className="text-xs bg-muted rounded-md p-2 text-muted-foreground">
            {meal.status_note}
          </p>
        )}

        {canUpdate && (
          <div className="grid grid-cols-3 gap-2 pt-1">
            {["arrived", "served", "issue"].map((next) => {
              const { label, icon: Icon } = STATUS[next];
              const active = meal.status === next;
              return (
                <Button
                  key={next}
                  size="sm"
                  variant={active ? "default" : "outline"}
                  className="h-11 gap-1.5"
                  onClick={() => onStatus(meal, next)}
                  data-testid={`mark-${next}-${meal.meal_id}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-xs">{label}</span>
                </Button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TodayPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { authAxios, canEdit, isVendor } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stale, setStale] = useState(null);   // age in ms when showing cached data

  const canUpdate = canEdit && !isVendor;

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setRefreshing(true);
    try {
      // Venue wifi drops. Falling back to the last good copy keeps the screen
      // usable, and the banner makes clear it is not live.
      const result = await fetchWithCache(`today:${eventId}`, async () => {
        const api = authAxios();
        const res = await api.get(`/events/${eventId}/today`);
        return res.data;
      });
      setData(result.data);
      setStale(result.stale ? result.ageMs : null);
    } catch {
      toast.error("Could not load today's schedule");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authAxios, eventId]);

  useEffect(() => { load(true); }, [load]);

  // Nobody on a busy day remembers to refresh.
  useEffect(() => {
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const setStatus = async (meal, status) => {
    // Optimistic: the person tapping is looking at the food, not the screen.
    setData((prev) => prev && {
      ...prev,
      meals: prev.meals.map((m) => (m.meal_id === meal.meal_id ? { ...m, status } : m)),
    });
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/meals/${meal.meal_id}/status`, { status });
      toast.success(`${meal.meal_type} marked ${status}`);
      load(true);
    } catch {
      toast.error("Saved on screen only — no connection. It will not be recorded.");
      load(true);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-24 bg-muted rounded-xl animate-pulse" />
        <div className="h-40 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!data?.is_event_day) {
    return (
      <div className="p-4 md:p-8 w-full text-center space-y-4" data-testid="today-not-running">
        <UtensilsCrossed className="h-10 w-10 mx-auto text-muted-foreground" />
        <div>
          <p className="font-semibold text-foreground">Nothing scheduled today</p>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.event_title} isn't running on {data?.date}.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/events/${eventId}`)}>
          Open the full plan
        </Button>
      </div>
    );
  }

  const { current, next, meals, counts } = data;

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-4 pb-24 print-sheet">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/events/${eventId}`)}
                className="gap-1.5 -ml-2 no-print" data-testid="today-back">
          <ArrowLeft className="h-4 w-4" />
          Plan
        </Button>
        <div className="flex items-center gap-1 no-print">
          <ShareButton getText={() => todaySummary(data)}
                       title={data?.event_title || "Today"}
                       testId="today-share" />
          {canUpdate && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5"
                    onClick={() => navigate(`/events/${eventId}/scan`)}
                    data-testid="today-scan">
              <UserCheck className="h-4 w-4" />
              Check-in
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => load()} disabled={refreshing}
                  aria-label="Refresh today's schedule" data-testid="today-refresh">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {stale != null && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
             data-testid="today-stale">
          <CloudOff className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-amber-700 dark:text-amber-500 leading-relaxed">
            <strong>Showing the last update from {describeAge(stale)}.</strong>{" "}
            You are offline, so status changes will not be saved until the connection returns.
          </p>
        </div>
      )}

      <div>
        <h1 className="text-xl font-bold text-foreground">{data.event_title}</h1>
        <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2">
          <span>Day {data.day_number}{data.day_name ? ` · ${data.day_name}` : ""}</span>
          {data.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" aria-hidden="true" />{data.location}
            </span>
          )}
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="today-counts">
        {Object.entries(STATUS).map(([key, { label }]) => (
          <Badge key={key} variant="secondary" className="text-xs">
            {label}: {counts?.[key] ?? 0}
          </Badge>
        ))}
      </div>

      {/* The whole reason this screen exists: what is happening right now. */}
      {current ? (
        <section className="space-y-2" data-testid="today-current">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Happening now</p>
          <MealRow meal={current} canUpdate={canUpdate} onStatus={setStatus} highlight />
        </section>
      ) : next ? (
        <section className="space-y-2" data-testid="today-next">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Up next</p>
          <MealRow meal={next} canUpdate={canUpdate} onStatus={setStatus} highlight />
        </section>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="today-all-done">
          Nothing else scheduled today.
        </p>
      )}

      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          All meals today
        </p>
        <div className="space-y-2">
          {meals.map((meal) => (
            <MealRow key={meal.meal_id} meal={meal} canUpdate={canUpdate} onStatus={setStatus} />
          ))}
          {meals.length === 0 && (
            <p className="text-sm text-muted-foreground">No meals planned for this day.</p>
          )}
        </div>
      </section>
    </div>
  );
}
